// src/components/LandingPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './LandingPage.css';
import { getQueryParts, validateLocationApi, createClickApi, getCID } from '../services/ApiAndLongServices';
import {
  ThermometerSun,
  ThermometerSnowflake,
  Fan,
  Flame,
  Thermometer,
  Layers,
  Gauge,
  Snowflake,
  Wrench,
} from 'lucide-react';

const buttonIcons = [
  {
    icon: ThermometerSnowflake,
    label: 'Central Air Conditioning Installation or Replacement',
    CategoryID: '135559407444107609',
  },
  {
    icon: Wrench,
    label: 'Central Air Conditioning Repair or Maintenance',
    CategoryID: '166577475042034098',
  },
  {
    icon: Fan,
    label: 'Duct and Vent Cleaning',
    CategoryID: '151452842457252122',
  },
  {
    icon: Wrench,
    label: 'Duct and Vent Installation or Removal',
    CategoryID: '261968547570794818',
  },
  {
    icon: Wrench,
    label: 'Duct and Vent Repair',
    CategoryID: '261968469074387265',
  },
  {
    icon: Flame,
    label: 'Fireplace and Chimney Cleaning or Repair',
    CategoryID: '151453853833978139',
  },
  {
    icon: Flame,
    label: 'Fireplace and Chimney Installation',
    CategoryID: '206936441702703407',
  },
  {
    icon: ThermometerSun,
    label: 'Heating System Installation or Replacement',
    CategoryID: '166573434891190694',
  },
  {
    icon: Thermometer,
    label: 'Heating System Repair or Maintenance',
    CategoryID: '166573479455678887',
  },
  {
    icon: Layers,
    label: 'Insulation Installation or Upgrade',
    CategoryID: '261970033189232981',
  },
  {
    icon: Gauge,
    label: 'Thermostat Installation or Repair',
    CategoryID: '151457230055776541',
  },
  {
    icon: Snowflake,
    label: 'Window, Wall, or Portable AC Repair or Maintenance',
    CategoryID: '206935519438151981',
  },
];


function isValidZip(zip) {
  return /^\d{5}$/.test(zip);
}

function LandingPage() {
  const nav = useNavigate();

  const [zipCode, setZipCode] = useState('');
  const [campaign, setCampaign] = useState('');

  const [zipError, setZipError] = useState('');
  const [trackingReady, setTrackingReady] = useState(false);


  useEffect(() => {
    createClickApi()
      .catch(err => console.error("Failed to create click:", err))
      .finally(() => setTrackingReady(true)); // always unblock
  }, []);

  const handleSubmit = async (label, CategoryID) => {
    if (!isValidZip(zipCode)) {
      setZipError('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (!trackingReady) {
      await new Promise(resolve => {
        const interval = setInterval(() => {
          if (getCID()) { clearInterval(interval); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(interval); resolve(); }, 3000);
      });
    }

    try {
      await validateLocationApi(zipCode);
      const trustedFormCertUrl = getTrustedFormCertUrl();
      nav('/results', {
        state: { zipCode, categoryId: CategoryID, trustedFormCertUrl, campaign }
      });
    } catch (e) {
      setZipError(e?.response?.data?.detail || e?.message || 'Invalid ZIP code');
    }
  };



  useEffect(() => {
    setCampaign(getQueryParts())
  }, []);

  useEffect(() => {
    if (document.getElementById("trustedform-script")) return;

    const tf = document.createElement("script");
    tf.id = "trustedform-script";
    tf.async = true;
    tf.src =
      `${document.location.protocol === "https:" ? "https" : "http"}://api.trustedform.com/trustedform.js` +
      `?field=xxTrustedFormCertUrl` +
      `&use_tagged_consent=true` +
      `&l=${Date.now()}`;

    document.body.appendChild(tf);
  }, []);

  const getTrustedFormCertUrl = () => {
    const el = document.getElementById("xxTrustedFormCertUrl");
    return el?.value || "";
  }


  return (
    <>
      <Header />

      <section className="hero" style={{
        backgroundImage: `
        linear-gradient(rgba(0, 43, 91, 0.8), rgba(0, 43, 91, 0.8)),url(/image.jpg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        <div className="hero__overlay" />
        <div className="hero__content">
          <h1 className="hero__title">
            Get Your Bathrom Remodeling Work Done{' '}
            <br className="hide-on-mobile" />
            with the Help of{' '}
            <span className="accent">Trusted Professionals</span>
          </h1>

          <div
            className="card"
            style={{
              padding: '2rem',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            <div
              className="form-group"
              style={{ marginBottom: '16px' }}
            >
              <label className="label" htmlFor="zip">
                ZIP Code
              </label>
              <input
                id="zip"
                inputMode="numeric"
                pattern="\d{5}"
                aria-invalid={!!zipError}
                aria-describedby={
                  zipError ? 'zip-error' : undefined
                }
                type="text"
                value={zipCode}
                onChange={(e) => {
                  const raw = e.target.value
                    .replace(/\D/g, '')
                    .slice(0, 5);
                  setZipCode(raw);
                  if (zipError && isValidZip(raw)) setZipError('');
                }}
                className={`input ${zipError ? 'input--invalid' : ''
                  }`}
                placeholder="Enter your ZIP code"
                maxLength={5}
                required
              />
              {zipError && (
                <p id="zip-error" className="error">
                  {zipError}
                </p>
              )}
            </div>

            <div
              className="ziplist-buttons"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                marginTop: '32px',
              }}
            >
              {buttonIcons.map(
                ({ icon: Icon, label, CategoryID }, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSubmit(label, CategoryID)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '32px',
                      borderRadius: '16px',
                      background: '#002b5b',
                      color: '#fff',
                      cursor: 'pointer',
                      border: 'none',
                      boxShadow:
                        '0 4px 6px rgba(0,0,0,0.1)',
                      transition:
                        'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform =
                        'scale(1.05)';
                      e.currentTarget.style.boxShadow =
                        '0 6px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform =
                        'scale(1)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 6px rgba(0,0,0,0.1)';
                    }}
                  >
                    <Icon
                      style={{
                        width: '48px',
                        height: '48px',
                        color: '#fff',
                        marginBottom: '8px',
                      }}
                    />
                    <span
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#fff',
                      }}
                    >
                      {label}
                    </span>
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </section>
      <form>
        <input
          type="hidden"
          id="xxTrustedFormCertUrl"
          name="xxTrustedFormCertUrl"
        />

      </form>
    </>
  );
}

export default LandingPage;
